import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase } from "@/lib/supabase";

interface CompanyProfile {
  id: string;
  slug: string;
  name: string;
  description: string;
  industry: string;
  company_size: string;
  location: string;
  website: string | null;
  founded: number | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  benefits: string[] | null;
  social_links: Record<string, string> | null;
}

interface JobItem {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  is_hot: boolean;
  description: string;
  posted_at: string;
  expiry_date: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function CompanyProfilePage() {
  const { slug } = useParams<{ slug: string }>();

  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [companyJobs, setCompanyJobs] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data: companyData, error: companyError } = await supabase
        .from("vietnamcv_company_profiles")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (companyError || !companyData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const comp = companyData as CompanyProfile;
      setCompany(comp);

      const { data: jobsData } = await supabase
        .from("vietnamcv_jobs")
        .select("*")
        .eq("status", "approved")
        .order("posted_at", { ascending: false });

      const matched = (jobsData || []).filter(
        (j: JobItem) => slugify(j.company) === comp.slug
      );
      setCompanyJobs(matched as JobItem[]);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar forceSolid />
        <main className="pt-20 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="relative h-48 md:h-64 bg-background-100 animate-pulse rounded-b-2xl mb-8"></div>
            <div className="space-y-6">
              <div className="flex gap-6 animate-pulse">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-background-200"></div>
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-background-200 rounded w-2/3"></div>
                  <div className="h-4 bg-background-200 rounded w-1/2"></div>
                  <div className="flex gap-2">
                    <div className="h-6 w-20 bg-background-200 rounded-full"></div>
                    <div className="h-6 w-28 bg-background-200 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !company) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar forceSolid />
        <main className="pt-20 pb-16">
          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-building-2-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-3">
              Không tìm thấy doanh nghiệp
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              Trang hồ sơ doanh nghiệp bạn đang tìm không tồn tại hoặc đã bị gỡ xuống.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Về trang chủ</span>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const typeLabels: Record<string, string> = {
    "full-time": "Toàn thời gian",
    "part-time": "Bán thời gian",
    contract: "Hợp đồng",
  };

  const logoInitials = company.name
    .split(" ")
    .filter((w) => w.length > 2 && !["CÔNG", "TY", "TNHH", "CỔ", "PHẦN"].includes(w.toUpperCase()))
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

  const benefits = company.benefits || [];
  const socialLinks = company.social_links || {};

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceSolid />

      <main>
        {/* Hero Banner */}
        <section className="relative pt-20">
          <div className="relative h-48 md:h-64 overflow-hidden bg-gradient-to-br from-accent-200/60 via-primary-100/50 to-background-100">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-primary-300/30 blur-3xl"></div>
              <div className="absolute bottom-0 right-20 w-80 h-80 rounded-full bg-accent-300/30 blur-3xl"></div>
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="relative -mt-16 md:-mt-20 flex flex-col md:flex-row md:items-end gap-4 md:gap-6 pb-6">
              {/* Logo */}
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-background-50 border-4 border-background-50 flex items-center justify-center flex-shrink-0 shadow-sm">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
                  <span className="font-heading font-extrabold text-3xl md:text-4xl text-primary-600">
                    {logoInitials || company.name.charAt(0)}
                  </span>
                </div>
              </div>

              <div className="flex-1 min-w-0 pt-2 md:pt-0 md:pb-2">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold">
                    <i className="ri-building-line text-xs"></i>
                    {company.industry}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs">
                    <i className="ri-team-line text-xs"></i>
                    {company.company_size}
                  </span>
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-1">
                  {company.name}
                </h1>
                <p className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-map-pin-line text-sm"></i>
                  {company.location}
                  {company.founded && (
                    <>
                      <span className="mx-1.5 text-foreground-300">|</span>
                      <i className="ri-calendar-line text-sm"></i>
                      Thành lập {company.founded}
                    </>
                  )}
                </p>
              </div>

              {company.email && (
                <div className="flex-shrink-0">
                  <a
                    href={`mailto:${company.email}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line"></i>
                    <span>Liên hệ tuyển dụng</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-10 md:py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
              {/* Left: About + Jobs */}
              <div className="flex-1 min-w-0">
                {/* About */}
                <div className="mb-10">
                  <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-information-line text-primary-500"></i>
                    Giới thiệu doanh nghiệp
                  </h2>
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70">
                    <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                      {company.description}
                    </p>
                  </div>
                </div>

                {/* Benefits */}
                {benefits.length > 0 && (
                  <div className="mb-10">
                    <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-gift-line text-accent-500"></i>
                      Quyền lợi nhân viên
                    </h2>
                    <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {benefits.map((benefit, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-6 h-6 flex items-center justify-center bg-accent-100 rounded-full flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-accent-600 text-xs"></i>
                            </div>
                            <span className="text-sm text-foreground-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Active Jobs */}
                {companyJobs.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-briefcase-line text-primary-500"></i>
                      Tin tuyển dụng đang mở
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                        {companyJobs.length}
                      </span>
                    </h2>

                    <div className="space-y-4">
                      {companyJobs.map((job) => (
                        <div
                          key={job.id}
                          className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 hover:border-primary-200/60 transition-all duration-300 group"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                            <h3 className="font-heading font-semibold text-base text-foreground-900">
                              {job.title}
                            </h3>
                            {job.is_hot && (
                              <span className="self-start sm:self-auto flex-shrink-0 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                                HOT
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <span className="inline-flex items-center gap-1 text-xs text-foreground-600 bg-secondary-100/70 px-2.5 py-1 rounded-full">
                              <i className="ri-map-pin-line text-xs"></i>
                              {job.location}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-accent-700 bg-accent-100/70 px-2.5 py-1 rounded-full font-medium">
                              <i className="ri-money-dollar-circle-line text-xs"></i>
                              {job.salary}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-foreground-500 bg-background-200/70 px-2.5 py-1 rounded-full">
                              <i className="ri-briefcase-4-line text-xs"></i>
                              {typeLabels[job.type] || job.type}
                            </span>
                          </div>

                          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                            {job.description}
                          </p>

                          <div className="flex items-center justify-between">
                            <span className="text-xs text-foreground-400">
                              <i className="ri-time-line mr-1"></i>
                              Đăng ngày {job.posted_at}
                            </span>
                            <span className="text-xs text-foreground-400">
                              <i className="ri-calendar-check-line mr-1"></i>
                              Hết hạn {job.expiry_date}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {companyJobs.length === 0 && (
                  <div className="text-center py-12 bg-background-100 rounded-2xl border border-background-200/70">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-background-200/70 flex items-center justify-center">
                      <i className="ri-inbox-line text-xl text-foreground-400"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">
                      Chưa có tin tuyển dụng
                    </h3>
                    <p className="text-sm text-foreground-500">
                      Hiện tại doanh nghiệp này chưa đăng tin tuyển dụng nào. Hãy quay lại sau nhé!
                    </p>
                  </div>
                )}
              </div>

              {/* Right: Sidebar */}
              <div className="lg:w-80 flex-shrink-0">
                <div className="lg:sticky lg:top-24 space-y-5">
                  {/* Contact Card */}
                  <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-contacts-line text-primary-500"></i>
                      Thông tin liên hệ
                    </h3>
                    <ul className="space-y-4">
                      {company.address && (
                        <li className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-background-50 rounded-lg flex-shrink-0">
                            <i className="ri-map-pin-line text-foreground-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400 mb-0.5">Địa chỉ</p>
                            <p className="text-sm text-foreground-700">{company.address}</p>
                          </div>
                        </li>
                      )}
                      {company.phone && (
                        <li className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-background-50 rounded-lg flex-shrink-0">
                            <i className="ri-phone-line text-foreground-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400 mb-0.5">Điện thoại</p>
                            <a
                              href={`tel:${company.phone}`}
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium no-underline"
                            >
                              {company.phone}
                            </a>
                          </div>
                        </li>
                      )}
                      {company.email && (
                        <li className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-background-50 rounded-lg flex-shrink-0">
                            <i className="ri-mail-line text-foreground-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400 mb-0.5">Email</p>
                            <a
                              href={`mailto:${company.email}`}
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium no-underline"
                            >
                              {company.email}
                            </a>
                          </div>
                        </li>
                      )}
                      {company.website && (
                        <li className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-background-50 rounded-lg flex-shrink-0">
                            <i className="ri-global-line text-foreground-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-400 mb-0.5">Website</p>
                            <a
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium no-underline"
                            >
                              {company.website.replace("https://", "")}
                            </a>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Stats Card */}
                  <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-bar-chart-2-line text-accent-500"></i>
                      Thống kê
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-background-50 rounded-xl">
                        <p className="font-heading font-bold text-xl text-foreground-950">
                          {companyJobs.length}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">Tin đang tuyển</p>
                      </div>
                      <div className="text-center p-3 bg-background-50 rounded-xl">
                        <p className="font-heading font-bold text-xl text-foreground-950">
                          {company.founded ? `${new Date().getFullYear() - company.founded}+` : "-"}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">Năm kinh nghiệm</p>
                      </div>
                      <div className="text-center p-3 bg-background-50 rounded-xl">
                        <p className="font-heading font-bold text-xl text-foreground-950">
                          {company.company_size.split(" ")[0]}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">Nhân viên</p>
                      </div>
                      <div className="text-center p-3 bg-background-50 rounded-xl">
                        <p className="font-heading font-bold text-xl text-foreground-950">
                          {company.location.split(",").pop()?.trim() || company.location}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">Khu vực</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}